// src/components/steps/PersonalInfoStep.js
import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";

export default forwardRef(function PersonalInfoStep({ setCanProceed }, ref) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [nascimento, setNascimento] = useState("");
  const [telefone, setTelefone] = useState("");
  const [cpf, setCpf] = useState("");
  const [cidade, setCidade] = useState("");
  const [uf, setUf] = useState("");

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // cálculo de hoje e data mínima para 18 anos
  const today = new Date();
  const eighteenAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );
  const maxBirthDate = eighteenAgo.toISOString().split("T")[0];

  // formatadores
  const formatPhone = (v) =>
    v
      .replace(/\D/g, "")
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d{1,4})/, "$1-$2")
      .slice(0, 15);

  const formatCpf = (v) =>
    v
      .replace(/\D/g, "")
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1-$2")
      .slice(0, 14);

  // validação
  const validateFields = () => {
    const errs = {};
    if (!nome) errs.nome = "O nome é obrigatório.";
    if (!email || !/\S+@\S+\.\S+/.test(email)) errs.email = "E‑mail inválido.";
    if (!nascimento) {
      errs.nascimento = "Data de nascimento obrigatória.";
    } else if (nascimento > maxBirthDate) {
      errs.nascimento = "Você deve ter pelo menos 18 anos.";
    }
    if (!telefone || telefone.replace(/\D/g, "").length < 10)
      errs.telefone = "Telefone inválido.";
    if (!cpf || cpf.replace(/\D/g, "").length !== 11) errs.cpf = "CPF inválido.";
    if (!cidade) errs.cidade = "Cidade obrigatória.";
    if (!uf) errs.uf = "Estado obrigatório.";
    return errs;
  };

  // expor getData e validateStep
  useImperativeHandle(ref, () => ({
    getData() {
      return {
        name: nome,
        email,
        birth_date: nascimento,
        phone: telefone,
        cpf_cnpj: cpf,
        city: cidade,
        state: uf,
      };
    },
    validateStep() {
      const errs = validateFields();
      setErrors(errs);
      return Object.keys(errs).length === 0;
    },
  }));

  // atualiza errors e canProceed
  useEffect(() => {
    const errs = validateFields();
    setErrors(errs);
    setCanProceed(Object.keys(errs).length === 0);
  }, [nome, email, nascimento, telefone, cpf, cidade, uf, setCanProceed]);

  // handlers de blur / touched
  const handleBlur = (field) => {
    setTouched((t) => ({ ...t, [field]: true }));
  };
  const handleChange = (field, value, formatter) => {
    const v = formatter ? formatter(value) : value;
    switch (field) {
      case "nome":
        setNome(v);
        break;
      case "email":
        setEmail(v);
        break;
      case "nascimento":
        setNascimento(v);
        break;
      case "telefone":
        setTelefone(v);
        break;
      case "cpf":
        setCpf(v);
        break;
      case "cidade":
        setCidade(v);
        break;
      case "uf":
        setUf(v);
        break;
    }
    if (touched[field]) {
      const errs = validateFields();
      if (!errs[field]) {
        setErrors((e) => {
          const { [field]: _, ...rest } = e;
          return rest;
        });
      }
    }
  };

  const inputClass =
    "w-full h-12 px-4 border border-[#E5E7EB] bg-[#F9FAFB] rounded-md placeholder-gray-400 text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A4DD0]";

  return (
    <div className="flex flex-col gap-6">
      {/* Nome */}
      <div>
        <label className="block font-semibold text-gray-900 mb-1">
          Nome Completo*
        </label>
        <input
          className={inputClass}
          value={nome}
          onBlur={() => handleBlur("nome")}
          onChange={(e) => handleChange("nome", e.target.value)}
          placeholder="Digite seu nome completo"
        />
        {touched.nome && errors.nome && (
          <p className="text-red-500 text-sm mt-1">{errors.nome}</p>
        )}
      </div>

      {/* E-mail */}
      <div>
        <label className="block font-semibold text-gray-900 mb-1">
          Email*
        </label>
        <input
          className={inputClass}
          type="email"
          value={email}
          onBlur={() => handleBlur("email")}
          onChange={(e) => handleChange("email", e.target.value)}
          placeholder="seuemail@email.com"
        />
        {touched.email && errors.email && (
          <p className="text-red-500 text-sm mt-1">{errors.email}</p>
        )}
      </div>

      {/* Nascimento & Telefone */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className="block font-semibold text-gray-900 mb-1">
            Nascimento*
          </label>
          <input
            className={inputClass}
            type="date"
            max={maxBirthDate}
            value={nascimento}
            onBlur={() => handleBlur("nascimento")}
            onChange={(e) => handleChange("nascimento", e.target.value)}
          />
          {touched.nascimento && errors.nascimento && (
            <p className="text-red-500 text-sm mt-1">
              {errors.nascimento}
            </p>
          )}
        </div>
        <div>
          <label className="block font-semibold text-gray-900 mb-1">
            Telefone*
          </label>
          <input
            className={inputClass}
            value={telefone}
            onBlur={() => handleBlur("telefone")}
            onChange={(e) =>
              handleChange("telefone", e.target.value, formatPhone)
            }
            placeholder="(00) 00000-0000"
          />
          {touched.telefone && errors.telefone && (
            <p className="text-red-500 text-sm mt-1">{errors.telefone}</p>
          )}
        </div>
      </div>

      {/* CPF */}
      <div>
        <label className="block font-semibold text-gray-900 mb-1">
          CPF*
        </label>
        <input
          className={inputClass}
          value={cpf}
          onBlur={() => handleBlur("cpf")}
          onChange={(e) => handleChange("cpf", e.target.value, formatCpf)}
          placeholder="000.000.000-00"
        />
        {touched.cpf && errors.cpf && (
          <p className="text-red-500 text-sm mt-1">{errors.cpf}</p>
        )}
      </div>

      {/* Cidade & UF */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className="block font-semibold text-gray-900 mb-1">
            Cidade*
          </label>
          <input
            className={inputClass}
            value={cidade}
            onBlur={() => handleBlur("cidade")}
            onChange={(e) => handleChange("cidade", e.target.value)}
            placeholder="Digite sua cidade"
          />
          {touched.cidade && errors.cidade && (
            <p className="text-red-500 text-sm mt-1">{errors.cidade}</p>
          )}
        </div>
        <div>
          <label className="block font-semibold text-gray-900 mb-1">
            UF*
          </label>
          <select
            className={`${inputClass} appearance-none`}
            value={uf}
            onBlur={() => handleBlur("uf")}
            onChange={(e) => handleChange("uf", e.target.value)}
          >
            <option value="">Selecione o estado</option>
            {[
              "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA",
              "MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN",
              "RS","RO","RR","SC","SP","SE","TO",
            ].map((estado) => (
              <option key={estado} value={estado}>
                {estado}
              </option>
            ))}
          </select>
          {touched.uf && errors.uf && (
            <p className="text-red-500 text-sm mt-1">{errors.uf}</p>
          )}
        </div>
      </div>
    </div>
  );
});
